import { useState } from 'react';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import { ProgressBar } from '@clay-run/glaze/progress-bar';
import {
  AlertDialog,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogActions,
} from '@clay-run/glaze/alert-dialog';
import { useBulkSubmit } from '../hooks/useBulkSubmit';
import type { Scan } from '../scan';

/**
 * "Submit N ready drafts" — plans/2026-09-18-001-lead-scanner-heic-bulk-submit-plan.md
 * §2. Renders nothing when there are no ready drafts in the caller's scoped
 * set (`ScanListCard.tsx` computes `readyDrafts` — currently filtered/visible
 * list, own rows only, `status === 'draft'` with non-empty name+company).
 *
 * One accidental tap otherwise creates N Salesforce records (Decision 4 of
 * the plan), so this always confirms first. Deliberately does NOT copy
 * `PhotoIntake.tsx`'s Enter-focuses-primary effect for its own "Scan N
 * leads" dialog — that one is a non-destructive confirm; this one fires N
 * real Salesforce submissions, so Base UI's default (focus on Cancel) is the
 * right one, same as every OTHER dialog in this app.
 */

interface BulkSubmitDraftsProps {
  /** The ready set — already filtered to `status === 'draft'`, non-empty
   *  trimmed name+company, and the viewer's own scans. */
  scans: Scan[];
}

export default function BulkSubmitDrafts({ scans }: BulkSubmitDraftsProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { inFlight, total, resolved, run } = useBulkSubmit();

  if (scans.length === 0) return null;

  const n = scans.length;

  return (
    // A single flex item in the header row's `flex items-center gap-2`
    // (`ScanListCard.tsx`) — `flex-col` so the progress block, when shown,
    // stacks under the button instead of squeezing into the same row as the
    // scan count. Exact narrow-viewport layout is deferred to browser QA
    // (plan §2 "Deferred to Implementation").
    <div className="flex flex-col items-end gap-2">
      <Button
        variant="secondary"
        size="sm"
        loading={inFlight}
        disabled={inFlight}
        onClick={() => setConfirmOpen(true)}
      >
        Submit {n} ready draft{n === 1 ? '' : 's'}
      </Button>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogHeader>Submit these drafts to Salesforce?</AlertDialogHeader>
        <AlertDialogBody>
          <Text size="sm">
            {n} draft{n === 1 ? ' has' : 's have'} a name and company filled in. Each is submitted exactly as
            it is now — open a draft first if you want to add an email, LinkedIn, or notes.
          </Text>
        </AlertDialogBody>
        <AlertDialogActions>
          <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setConfirmOpen(false);
              void run(scans);
            }}
          >
            Submit {n}
          </Button>
        </AlertDialogActions>
      </AlertDialog>
      {inFlight && (
        <div className="flex w-full flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <Text size="sm" weight="semibold">
              Submitting drafts
            </Text>
            <Text size="xs" variant="tertiary">
              {resolved} of {total}
            </Text>
          </div>
          <ProgressBar percentage={(resolved / total) * 100} />
        </div>
      )}
    </div>
  );
}
